/**
 * BaseExecutiveDashboard — the layout engine used by both DashboardScope12
 * and DashboardScope123 variants. All section composition lives here; the
 * variants only flip the `hasScope3` flag and trim the rows accordingly.
 *
 * Data contract: takes the output of `useDashboardData` AS-IS plus a
 * `targets` array from the targets API. No API calls happen here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Activity, BarChart3, Flame, Leaf, Loader2, RadioTower, TreeDeciduous } from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import StickyFilterBar from './components/filters/StickyFilterBar';
import SectionCard from './components/layout/SectionCard';
import KpiCard from './components/kpi/KpiCard';
import GaugeCard from './components/kpi/GaugeCard';
import ScopeTrendChart from './components/charts/ScopeTrendChart';
import EmissionsByScopeDonut from './components/charts/EmissionsByScopeDonut';
import FacilityChart from './components/charts/FacilityChart';
import EmissionsByScopeCategoryList from './components/charts/EmissionsByScopeCategoryList';
import GeoHeatmap from './components/charts/GeoHeatmap';
import BaseYearComparisonChart from './components/charts/BaseYearChart';
import { DashboardExportButton } from './pdf-export';
import {
  buildSparklineSeries,
  buildEmissionsByScope,
  buildFacilitySeries,
  buildScopedCategoryList,
  buildHeatPoints,
  buildBaseYearChartData,
} from './services/dataTransformers';
import usePreviousYearData from './services/fetchPreviousYearData';
import { useIntensityData } from './hooks/useIntensityData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BaseExecutiveDashboard({ data, hasScope3 }) {
  const { getAuthHeader } = useAuth();
  const {
    stats, loading, organization, facilities,
    selectedFacilities, setSelectedFacilities,
    dateRange, setDateRange,
    showFilters, setShowFilters,
    showFacilityDropdown, setShowFacilityDropdown, facilityDropdownRef,
    filteredData, baseYearComparison,
    isLive, lastLiveUpdateAt, getCurrentFinancialYear,
  } = data;

  const [heatmapView, setHeatmapView] = useState('india');
  // Targets — fetched once on mount. Errors swallowed (gauge has empty state).
  const [targets, setTargets] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch GHG targets from esg_targets where category is "GHG Emissions"
        const res = await axios.get(`${API}/esg-targets/with-progress?section=environment&category=GHG Emissions&status=active`, { headers: getAuthHeader() });
        if (!cancelled) {
          // Transform esg_targets format to match expected target format for gauge card
          const ghgTargets = (res.data || []).map(t => {
            return {
              id: t.id,
              name: t.target_name || t.name || t.kpi_name || t.subcategory || 'Untitled target',
              kpi_id: t.kpi_id,
              category: t.category,
              subcategory: t.subcategory,
              _progressPct: t.progress_percentage,
              actualValue: t.actual_value,
              targetValue: t.target_value,
              goalType: t.goal_type,
              unit: t.unit,
              reportingPeriod: t.reporting_period,
            };
          });
          setTargets(ghgTargets);
        }
      } catch { /* leave empty */ }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  const previousYearTotals = usePreviousYearData({
    dateRange,
    selectedFacilities,
    getAuthHeader,
  });
  // --- derived chart data ---
  const totals = filteredData.totals;
  // const trendDeltas = useMemo(() => deriveTrendDeltas(filteredData.trend), [filteredData.trend]);
  const totalSparkData = useMemo(() => buildSparklineSeries(filteredData.trend, 'total'), [filteredData.trend]);
  const donutData = useMemo(() => buildEmissionsByScope(totals, hasScope3), [totals, hasScope3]);
  const facilitySeries = useMemo(() => buildFacilitySeries(filteredData.facilities), [filteredData.facilities]);
  const scopedCategoryList = useMemo(() => buildScopedCategoryList(stats?.emissions_by_category), [stats]);
  const baseYearChart = useMemo(() => buildBaseYearChartData(baseYearComparison, totals, hasScope3), [baseYearComparison, totals, hasScope3]);
  const heatPoints = useMemo(() => buildHeatPoints(facilities, filteredData.facilities), [facilities, filteredData.facilities]);
  const trendDeltas = useMemo(() => {
      const computePct = (current = 0, previous = 0) => {
        if (!previous || previous === 0) return null;

        return ((current - previous) / previous) * 100;
      };

      // current year
      const currentNetEmissions =
        (totals.total || 0) - (filteredData.filteredSinks || 0);

      // previous year
      const previousNetEmissions =
        (previousYearTotals?.totalEmissions || 0) -
        (previousYearTotals?.totalSinks || 0);

      return {
        totalDelta: computePct(
          totals.total,
          previousYearTotals?.totalEmissions || 0
        ),

        sinksDelta: computePct(
          filteredData.filteredSinks || 0,
          previousYearTotals?.totalSinks || 0
        ),

        netDelta: computePct(
          currentNetEmissions,
          previousNetEmissions
        ),
      };
  }, [totals, filteredData, previousYearTotals]);

  const sinksTotal = filteredData.filteredSinks || 0;
  const netEmissions = (totals.total || 0) - sinksTotal;
  const { turnover, productionQty, productionUnit, loading: intensityLoading } = useIntensityData(dateRange, selectedFacilities);
  const [intensityMode, setIntensityMode] = useState('revenue');
  const scope12Emissions = (totals.scope1 || 0) + (totals.scope2 || 0);
  const intensityValue = intensityMode === 'revenue'
    ? (turnover && turnover > 0 ? scope12Emissions / turnover : null)
    : (productionQty && productionQty > 0 ? scope12Emissions / productionQty : null);
  const intensityUnit = intensityMode === 'revenue' ? 'tCO₂e/Cr' : `tCO₂e/${productionUnit || 'unit'}`;

  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const selectedTarget =
    targets.find((t) => t.id === selectedTargetId) ||
    targets?.[0];
  const targetProgressPct = selectedTarget?._progressPct;

  const dateRangeLabel = dateRange?.from && dateRange?.to
    ? `${format(dateRange.from, 'MMM yyyy')} – ${format(dateRange.to, 'MMM yyyy')}`
    : 'All time';

  const comparisonLabel = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return null;
    const currentFrom = new Date(dateRange.from);
    const currentTo = new Date(dateRange.to);
    const isSingleMonth = currentFrom.getFullYear() === currentTo.getFullYear()
      && currentFrom.getMonth() === currentTo.getMonth();

    if (isSingleMonth) return 'Compared with previous month';

    const now = new Date();
    const currentFinancialYearStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const isCurrentFinancialYear = currentFrom.getFullYear() === currentFinancialYearStart
      && currentFrom.getMonth() === 3
      && currentTo.getFullYear() === currentFinancialYearStart + 1
      && currentTo.getMonth() === 2;
    if (isCurrentFinancialYear) return 'Compared with previous FY';

    const isCurrentCalendarYear = currentFrom.getFullYear() === now.getFullYear()
      && currentFrom.getMonth() === 0
      && currentTo.getFullYear() === now.getFullYear()
      && currentTo.getMonth() === 11;
    if (isCurrentCalendarYear) return 'Compared with previous CY';

    const previousFrom = new Date(currentFrom);
    const previousTo = new Date(currentTo);
    previousFrom.setFullYear(previousFrom.getFullYear() - 1);
    previousTo.setFullYear(previousTo.getFullYear() - 1);
    return `Compared with ${format(previousFrom, 'MMM yyyy')} – ${format(previousTo, 'MMM yyyy')}`;
  }, [dateRange]);

  const filterProps = {
    facilities, selectedFacilities, setSelectedFacilities,
    dateRange, setDateRange,
    showFacilityDropdown, setShowFacilityDropdown, facilityDropdownRef,
    getCurrentFinancialYear,
  };

  const liveBadge = isLive ? (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100/70 border border-emerald-200 rounded-full px-2 py-0.5"
      title={lastLiveUpdateAt ? `Updated ${format(lastLiveUpdateAt, 'HH:mm:ss')}` : ''}
    >
      <RadioTower className="w-3 h-3" />
      Live
    </span>
  ) : null;

  return (
    <div className="w-full max-w-full space-y-6 overflow-x-visible pb-0" data-testid="executive-dashboard">
      <StickyFilterBar
        title={organization?.name ? `${organization.name} · GHG Dashboard` : 'GHG Dashboard'}
        subtitle={`Reporting window: ${dateRangeLabel}`}
        liveBadge={liveBadge}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterProps={filterProps}
        dashboardType={data.dashboardType}
        setDashboardType={data.setDashboardType}
        esgSection={data.esgSection}
        setEsgSection={data.setEsgSection}
        showDashboardToggle={data.showDashboardToggle}
        organizationLogo={organization?.logo}
        organizationName={organization?.name}
        showExport
        exportButton={
          <DashboardExportButton
            dashboardType="ghg"
            data={{
              emissions: {
                scope1: totals.scope1,
                scope2: totals.scope2,
                scope3: hasScope3 ? totals.scope3 : 0,
                biogenic: totals.biogenic,
              },
              analytics: stats,
              trends: filteredData.trend,
              previousYear: previousYearTotals,
              targets,
              baseYear: baseYearChart,
            }}
            organization={organization}
            dateRange={dateRange}
            facilities={facilities}
          />
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-16 text-stone-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading dashboard…
        </div>
      ) : (
        <>
          <div className="space-y-4">
          {/* ROW 1: KPI cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-[repeat(5,minmax(0,0.68fr))_minmax(0,0.82fr)]">
            <KpiCard
              title="Total Emissions"
              value={totals.total}
              deltaPct={trendDeltas.totalDelta}
              sparkData={totalSparkData}
              sparkColor="#10B981"
              icon={Flame}
              comparisonLabel={comparisonLabel}
            />
            <KpiCard
              title="Sinks"
              value={sinksTotal}
              deltaPct={trendDeltas.sinksDelta}
              sparkData={[]}
              sparkColor="#0EA5E9"
              icon={TreeDeciduous}
              invertedColor
              comparisonLabel={comparisonLabel}
            />
            <KpiCard
              title="Net Emissions"
              value={netEmissions}
              deltaPct={trendDeltas.netDelta}
              sparkData={totalSparkData}
              sparkColor="#F59E0B"
              icon={Activity}
              comparisonLabel={comparisonLabel}
            />
            <KpiCard
              title="GHG Intensity"
              value={intensityValue}
              unit={intensityUnit}
              decimals={4}
              sparkColor="#0F766E"
              icon={BarChart3}
              loading={intensityLoading}
              emptyLabel={intensityMode === 'revenue' ? 'Revenue unavailable' : 'Production unavailable'}
              rightSlot={<select value={intensityMode} onChange={(event) => setIntensityMode(event.target.value)} className="w-[84px] rounded-md border border-stone-200 bg-white px-1.5 py-1 text-[10px] font-medium text-stone-600" data-testid="ghg-intensity-mode-selector" aria-label="GHG intensity calculation basis"><option value="revenue">Revenue</option><option value="production">Production</option></select>}
              rightSlotBelow
            />
            <KpiCard
              title="Avoided Emissions"
              value={stats?.avoided_emissions_green_energy || 0}
              sparkData={[]}
              sparkColor="#16A34A"
              icon={Leaf}
              invertedColor
              ariaLabel="Avoided emissions due to renewable Scope 2 electricity"
            />
            <GaugeCard
              targets={targets}
              selectedTarget={selectedTarget}
              selectedTargetId={selectedTargetId}
              setSelectedTargetId={setSelectedTargetId}
              progressPercentage={targetProgressPct}
            />
          </div>

          {/* ROW 2: Trend + Donut */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
             <SectionCard
              className="lg:col-span-3"
              title={hasScope3 ? 'Scope 1, 2 & 3 Emissions Trend' : 'Scope 1 & 2 Emissions Trend'}
              subtitle="Emissions over reporting period"
              accent="#10B981"
              testId="section-scope-trend"
            >
              <ScopeTrendChart data={filteredData.trend} hasScope3={hasScope3} />
            </SectionCard>

            <SectionCard
              title="Emissions by Scope"
              subtitle="Share of total"
              accent="#3B82F6"
              testId="section-emissions-by-scope"
            >
              <EmissionsByScopeDonut data={donutData} />
            </SectionCard>
          </div>

          {/* ROW 3: Operational hotspots */}
          <div className="grid grid-cols-1 items-stretch lg:grid-cols-2 gap-3">
            <SectionCard className="flex min-h-[450px] flex-col" title="Facility-wise Emissions" subtitle="Top contributors" accent="#34D399" testId="section-facility" contentClassName="flex flex-1 flex-col pb-0">
              <FacilityChart facilities={facilitySeries} height="100%" className="min-h-[400px] flex-1" showScope3={hasScope3} />
            </SectionCard>

            <SectionCard accent="#0F766E" testId="section-emissions-by-category">
              <EmissionsByScopeCategoryList data={scopedCategoryList} showScope3={hasScope3} />
            </SectionCard>
          </div>

          {/* ROW 4: BaseYearChart + Heatmap */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <SectionCard
              className="lg:col-span-3"
              title="Base Year vs Current Year"
              subtitle="Emissions comparison by scope"
              accent="#0F766E"
              testId="section-base-comparison"
            >
              <BaseYearComparisonChart data={baseYearChart.rows} />
            </SectionCard>

            <SectionCard
              className="lg:col-span-2"
              accent="#EF4444"
              testId="section-heatmap"
              header={
                <div className="flex items-start justify-between w-full">
                  <div>
                    <h3 className="text-sm font-semibold text-stone-900">
                      Geographic Heatmap
                    </h3>

                    <p className="text-xs text-stone-500 mt-0.5">
                      Facility emission concentration
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setHeatmapView('india')}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        heatmapView === 'india'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-primary/30 hover:text-primary'
                      }`}
                      data-testid="heatmap-toggle-india"
                    >
                      India
                    </button>

                    <button
                      onClick={() => setHeatmapView('global')}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        heatmapView === 'global'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-primary/30 hover:text-primary'
                      }`}
                      data-testid="heatmap-toggle-global"
                    >
                      Global
                    </button>
                  </div>
                </div>
              }
            >
              <GeoHeatmap
                points={heatPoints}
                view={heatmapView}
              />
            </SectionCard>

          </div>
          </div>
        </>
      )}
    </div>
  );
}
